import { SelfServiceUserInfoDto } from '@ekt-group/general-purpose-api-interfaces';
import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { fetchFn } from '@/lib/react-query';
import { useUserInfoStore } from '@/store';

const authRoutes = {
  login: '/self-service/auth/login',
  logout: '/self-service/auth/logout',
  verify: '/self-service/auth/callback',
  me: '/self-service/auth/me',
  getSessionToken: '/self-service/auth/session',
};

const getUserFn = async (): Promise<SelfServiceUserInfoDto> => {
  return fetchFn(authRoutes.me);
};

interface GetSessionTokenResponse {
  sessionToken: string;
}

const getSessionToken = (): Promise<GetSessionTokenResponse> => {
  return fetchFn(authRoutes.getSessionToken);
};

const logInFn = async (accessToken: string): Promise<SelfServiceUserInfoDto> => {
  return fetchFn(authRoutes.login, {
    params: {
      accessToken,
    },
  });
};

const logOutFn = async () => {
  return fetchFn(authRoutes.logout);
};

const configureEmbeddedAuth = () => {
  const useLazyGetSessionToken = (options?: Omit<UseMutationOptions<GetSessionTokenResponse>, 'mutationFn'>) => {
    return useMutation({
      mutationFn: getSessionToken,
      ...options,
    });
  };

  const useEmbeddedLogin = (options?: Omit<UseMutationOptions<SelfServiceUserInfoDto, Error, string>, 'mutationFn'>) => {
    const queryClient = useQueryClient();
    const { selectedAccount, setSelectedAccount } = useUserInfoStore();

    return useMutation({
      mutationFn: logInFn,
      onSuccess: (data, ...rest) => {
        queryClient.setQueryData(['user'], data);
        if (data?.accounts?.length) {
          const foundSelectedAccount = data.accounts.find(({ code }) => code === selectedAccount?.code);
          if (!foundSelectedAccount) {
            setSelectedAccount(data.accounts[0]);
          }
          return data;
        }
        options?.onSuccess?.(data, ...rest);
      },
      ...options,
    });
  };

  const useEmbeddedUser = (options?: Omit<UseQueryOptions<SelfServiceUserInfoDto>, 'queryKey' | 'queryFn'>) => {
    const { selectedAccount, setSelectedAccount, setHasSelectedAccountFirstTime } = useUserInfoStore();

    return useQuery({
      queryKey: ['user'],
      queryFn: async () => {
        try {
          const data = await getUserFn();
          if (data?.accounts?.length) {
            const foundSelectedAccount = data.accounts.find(({ code }) => code === selectedAccount?.code);
            if (!foundSelectedAccount) {
              setSelectedAccount(data.accounts[0]);
            }
            return data;
          }
        } catch (error) {
          setHasSelectedAccountFirstTime(false);
        }
      },
      ...options,
    });
  };

  const useEmbeddedLogout = (options?: UseMutationOptions) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: logOutFn,
      onSuccess: (...rest) => {
        queryClient.removeQueries({
          queryKey: ['user'],
        });
        options?.onSuccess?.(...rest);
      },
      ...options,
    });
  };

  return {
    useLazyGetSessionToken,
    useEmbeddedUser,
    useEmbeddedLogin,
    useEmbeddedLogout,
  };
};

export const { useLazyGetSessionToken, useEmbeddedLogin, useEmbeddedLogout, useEmbeddedUser } = configureEmbeddedAuth();
